import microMemoize from "micro-memoize";
import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import type {
  Components,
  Options as ReactMarkdownOptions,
} from "react-markdown";
import type { TransformLinkTarget } from "react-markdown/lib/ast-to-react";
import type { PluggableList } from "react-markdown/lib/react-markdown";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import rehypeSlug from "rehype-slug";
import remarkBreaks from "remark-breaks";
import remarkGFM from "remark-gfm";
import remarkMath from "remark-math";
import styled from "styled-components";

// needed for rehype-katex to properly render
import "katex/dist/katex.min.css";

import { useScrollOffset } from "../../hooks/useScrollOffset";
import { useIsEmbeddedUrl } from "../../route/routeHooks";
import { MarkdownSize } from "../../theme/common/theme";
import { createRequestHeaders } from "../../util/headers.js";
import { TestSelectors } from "../../util/testSelectors";

import {
  MarkdownFrontmatterDisplayOptions,
  markdownFrontmatterDisplay,
  markdownFrontmatterDisplayStyles,
} from "./markdownFrontmatterDisplay";
import { MarkdownSanitizeSchema } from "./MarkdownSanitizeSchema";
import {
  MARKDOWN_AND_RICH_TEXT_HEADING_LINK_CLASSNAME,
  ScrollToLinkAnchor,
} from "./ScrollToLinkAnchor";
import { SyntaxHighlighter } from "./SyntaxHighlighter";

export const MARKDOWN_HEADING_DATA_ATTRIBUTE = "data-markdown-heading" as const;

export const MARKDOWN_LINK_DATA_ATTRIBUTE = "data-markdown-link" as const;

const transformLinkTarget: TransformLinkTarget = (href, _children, _title) =>
  href.startsWith("#")
    ? // if a hash link, make sure we don't open another tab
      "_self"
    : "_blank";

const createAnchorComponent = (isEmbeddedApp: boolean): Components["a"] => {
  const anchorRenderer: Components["a"] = ({
    children,
    className,
    node: _node,
    ...props
  }) =>
    className?.includes(MARKDOWN_AND_RICH_TEXT_HEADING_LINK_CLASSNAME) ? (
      <ScrollToLinkAnchor
        className={className}
        {...props}
        isEmbeddedApp={isEmbeddedApp}
      />
    ) : (
      <a
        className={className}
        {...({
          [MARKDOWN_LINK_DATA_ATTRIBUTE]: "true",
        } as Record<string, string>)}
        {...props}
      >
        {children}
      </a>
    );

  return anchorRenderer;
};

// see https://github.com/remarkjs/react-markdown#use-custom-components-syntax-highlight
const codeRenderer: Components["code"] = ({
  children,
  className,
  inline,
  node: _node,
  ...props
}) =>
  inline !== true ? (
    <SyntaxHighlighter
      copyable={true}
      isInAppView={true}
      language={className?.match(/language-(\w+)/)?.[1]}
      {...props}
    >
      {String(children).replace(/^\n|\n$/g, "")}
    </SyntaxHighlighter>
  ) : (
    <code className={className} {...props}>
      {children}
    </code>
  );

/**
 * Predownloads images hosted by us and renders them via createObjectURL.
 * This allows us to pipe in bearer token auth for anonymous users without cookies.
 */
export const PredownloadImageTag: React.FunctionComponent<
  React.DetailedHTMLProps<
    React.ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
  >
> = React.memo(function PredownloadedImageRenderer({ src, ...props }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;
    const makeObjectUrl = async (originUrl: string): Promise<void> => {
      const response = await fetch(originUrl, {
        headers: createRequestHeaders(),
      });
      if (isCancelled) return;
      if (response.ok) {
        blobUrl = window.URL.createObjectURL(await response.blob());
      }
      setObjectUrl(blobUrl);
      setIsLoading(false);
    };

    let blobUrl: string | null = null;

    if (src && src.startsWith("/api/v1/file/")) {
      void makeObjectUrl(src);
    } else {
      setObjectUrl(src || null);
      setIsLoading(false);
    }

    return () => {
      isCancelled = true;
      if (blobUrl) {
        window.URL.revokeObjectURL(blobUrl);
      }
    };
  }, [src]);

  if (isLoading) {
    // todo(nwold): should this be a loading spinner?
    return <img {...props} />;
  } else {
    // @ts-expect-error - null is a valid value here, it just shows the broken image icon
    return <img src={objectUrl} {...props} />;
  }
});

const imageRenderer: Components["img"] = ({ ...props }) => (
  <PredownloadImageTag {...props} />
);

const StyledHeaderBase = styled.h1<{ scrollOffset: number }>`
  scroll-margin-top: ${(props) => `${props.scrollOffset}px`};
`;

const withOffset = <T extends "h1" | "h2" | "h3" | "h4" | "h5" | "h6">(
  headingType: T,
  scrollOffset: number,
): Components[T] => {
  return function HeaderWithScrollMargin({ children, node: _node, ...props }) {
    return (
      <StyledHeaderBase
        {...({
          [MARKDOWN_HEADING_DATA_ATTRIBUTE]: headingType,
        } as Record<string, string>)}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ts isn't inferring this type correctly and thinks `as?: never | undefined`
        as={headingType as any}
        scrollOffset={scrollOffset}
        {...props}
      >
        {children}
      </StyledHeaderBase>
    );
  };
};

const defaultComponents: ReactMarkdownOptions["components"] = {
  code: codeRenderer,
  img: imageRenderer,
};

const createComponentsWithOffsets: (
  scrollOffset: number,
) => ReactMarkdownOptions["components"] = (scrollOffset) => ({
  h1: withOffset("h1", scrollOffset),
  h2: withOffset("h2", scrollOffset),
  h3: withOffset("h3", scrollOffset),
  h4: withOffset("h4", scrollOffset),
  h5: withOffset("h5", scrollOffset),
  h6: withOffset("h6", scrollOffset),
});

const remarkPlugins = microMemoize(
  (softLineBreaks = false, singleTildeStrikethrough = true): PluggableList => [
    // adds strike through, table support, tasklists, and some other goodies
    [remarkGFM, { singleTilde: singleTildeStrikethrough }],
    // allows parsing of math in markdown
    [remarkMath, { singleDollarTextMath: false }],
    ...(softLineBreaks ? [remarkBreaks] : []),
    // parses frontmatter and renders it as grey text instead of hiding it
    [
      markdownFrontmatterDisplay,
      { type: "yaml", marker: "-" } satisfies MarkdownFrontmatterDisplayOptions,
    ],
  ],
  { maxSize: 4 },
);

const rehypePluginsNoAutolink: PluggableList = [
  // allows HTML to pass through
  rehypeRaw,
  // make all the above plugins safe MUST BE AFTER USER HTML - https://github.com/rehypejs/rehype-sanitize#security
  [rehypeSanitize, MarkdownSanitizeSchema],
  // pretty renders math
  rehypeKatex,
];

// this plugin chokes on array spread syntax
// eslint-disable-next-line tree-shaking/no-side-effects-in-initialization
const rehypePluginsWithAutolink: PluggableList = rehypePluginsNoAutolink.concat(
  [
    // add ids to headings, required for rehype-autolink-headings
    rehypeSlug,
    // allow direct linking to headings
    [
      rehypeAutolinkHeadings,
      {
        behavior: "append",
        properties: {
          ariaHidden: "true",
          tabIndex: "-1",
          class: MARKDOWN_AND_RICH_TEXT_HEADING_LINK_CLASSNAME,
        },
      },
    ],
  ],
);

const MarkdownBody = styled(
  ReactMarkdown as React.ComponentType<ReactMarkdownOptions>,
)<{
  $size: MarkdownSize;
  $muted?: boolean;
  $inline?: boolean;
  $disableThemeStyles?: boolean;
}>`
  overflow: hidden;
  ${({ $disableThemeStyles, $size, theme }) =>
    $disableThemeStyles ? undefined : theme.markdownStyles($size)}
  ${({ $muted, theme }) =>
    $muted &&
    `
      color: ${theme.fontColor.MUTED};
    `}
  ${({ $inline }) =>
    $inline &&
    // use inline-block and override some styles to reduce overall height
    // when it fits in one line
    `
      display: inline-block;
      p {
        padding: 0;
      }
      code {
        line-height: unset;
        padding: 0 2px;
      }
    `}

  ${markdownFrontmatterDisplayStyles}
`;

export interface MarkdownRendererProps {
  size: MarkdownSize;
  className?: string;
  children: string;
  muted?: boolean;
  components?: ReactMarkdownOptions["components"];
  additionalRemarkPlugins?: PluggableList;
  disableThemeStyles?: boolean;

  /**
   * If false, tildes will be rendered literally instead of converted to strikethrough
   * when processed by remarkGFM.
   */
  singleTildeStrikethrough?: boolean;

  /** If true, will not turn link-like text into clickable links. */
  disableAutolink?: boolean;
  /**
   * Renders soft line breaks (`\n`) as hard line breaks (`\n\n`).
   * This setting is useful if you want to display user content closer to how it
   * was authored, particularly if it was authored in a wrapping editor,
   * because when a user includes a line ending, it’ll show as such.
   *
   * This is a somewhat popular unofficial variant of markdown in practice,
   * despite not being standard in the spec. For example, Slack and GitHub do this.
   */
  softLineBreaks?: boolean;
  /**
   * If true, the markdown renderer will be inline-block, and will try to avoid
   * vending additional line height. Useful when embedding markdown content
   * inside of other text.
   */
  inline?: boolean;
}

export const MarkdownRenderer: React.ComponentType<MarkdownRendererProps> =
  React.memo(function MarkdownRenderer({
    additionalRemarkPlugins,
    children,
    className,
    components: componentsOverride,
    disableAutolink = false,
    disableThemeStyles = false,
    inline = false,
    muted = false,
    singleTildeStrikethrough = true,
    size,
    softLineBreaks = false,
  }) {
    const scrollOffset = useScrollOffset();
    const isEmbeddedApp = useIsEmbeddedUrl();
    const headingComponents = React.useMemo(() => {
      return createComponentsWithOffsets(scrollOffset ?? 0);
    }, [scrollOffset]);
    const anchorComponents = React.useMemo(() => {
      return {
        a: createAnchorComponent(isEmbeddedApp),
      };
    }, [isEmbeddedApp]);
    const baseRemarkPlugins = remarkPlugins(
      softLineBreaks,
      singleTildeStrikethrough,
    );
    const combinedRemarkPlugins = additionalRemarkPlugins?.length
      ? [...baseRemarkPlugins, ...additionalRemarkPlugins]
      : baseRemarkPlugins;

    return (
      <MarkdownBody
        $disableThemeStyles={disableThemeStyles}
        $inline={inline}
        $muted={muted}
        $size={size}
        className={className}
        components={{
          ...anchorComponents,
          ...defaultComponents,
          ...headingComponents,
          ...(componentsOverride ?? {}),
        }}
        data-test={TestSelectors.MARKDOWN_CONTAINER}
        linkTarget={transformLinkTarget}
        rehypePlugins={
          disableAutolink ? rehypePluginsNoAutolink : rehypePluginsWithAutolink
        }
        remarkPlugins={combinedRemarkPlugins}
      >
        {children}
      </MarkdownBody>
    );
  });
